import { Card, Col, Empty, Row, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TFunction } from 'i18next';
import {
    buildIncomingSummary,
    type IncomingSummaryGroup,
    type IncomingSummaryMetal,
    type IncomingSummaryRow,
    type IncomingSummarySection,
} from '../../domain/metals/incomingSummary';

const { Title, Text } = Typography;

type Props = {
    t: TFunction;
    groups: IncomingSummaryGroup[];
};

type NumericField = 'quantity' | 'fineQuantity' | 'valueEur' | 'valueRon';

const METAL_TAG_COLOR: Record<IncomingSummaryMetal, string> = {
    gold: 'gold',
    silver: 'default',
    other: 'blue',
};

// Metal quantities to 2 decimals, money to 0 (CLAUDE.md "Metal Quantities & Currency Display").
const FIELD_DIGITS: Record<NumericField, number> = {
    quantity: 2,
    fineQuantity: 2,
    valueEur: 0,
    valueRon: 0,
};

function formatNumber(value: number, digits: number): string {
    return value.toLocaleString('ro-RO', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
}

function numericColumn(
    field: NumericField,
    title: string,
): ColumnsType<IncomingSummaryRow>[number] {
    return {
        key: field,
        dataIndex: field,
        title,
        align: 'right',
        render: (value: number) => formatNumber(value, FIELD_DIGITS[field]),
    };
}

function sectionColumns(
    t: TFunction,
    metal: IncomingSummaryMetal,
): ColumnsType<IncomingSummaryRow> {
    return [
        {
            key: 'label',
            dataIndex: 'label',
            title: t('metals.incomingSummary.purity'),
            render: (label: string, row: IncomingSummaryRow) => (
                <Space size={8}>
                    <Text strong>{label}</Text>
                    <Tag color={METAL_TAG_COLOR[metal]}>{row.percent}</Tag>
                </Space>
            ),
        },
        numericColumn('quantity', t('metals.incomingSummary.quantity')),
        numericColumn('fineQuantity', t(`metals.incomingSummary.fine.${metal}`)),
        numericColumn('valueEur', t('metals.incomingSummary.valueEur')),
        numericColumn('valueRon', t('metals.incomingSummary.valueRon')),
    ];
}

function SectionTotals({
    t,
    section,
}: Readonly<{ t: TFunction; section: IncomingSummarySection }>) {
    const fields: NumericField[] = ['quantity', 'fineQuantity', 'valueEur', 'valueRon'];
    return (
        <Table.Summary.Row>
            <Table.Summary.Cell index={0}>
                <Text strong>{t('metals.incomingSummary.total')}</Text>
            </Table.Summary.Cell>
            {fields.map((field, position) => (
                <Table.Summary.Cell key={field} index={position + 1} align="right">
                    <Text strong>{formatNumber(section[field], FIELD_DIGITS[field])}</Text>
                </Table.Summary.Cell>
            ))}
        </Table.Summary.Row>
    );
}

function SectionCard({ t, section }: Readonly<{ t: TFunction; section: IncomingSummarySection }>) {
    return (
        <Card
            size="small"
            data-testid={`incoming-summary-${section.metal}`}
            title={
                <Tag
                    color={METAL_TAG_COLOR[section.metal]}
                    style={{ fontSize: 14, padding: '2px 10px' }}
                >
                    {t(`metals.incomingSummary.metal.${section.metal}`)}
                </Tag>
            }
            extra={
                <Text type="secondary">
                    {t('metals.incomingSummary.pmp')}:{' '}
                    <Text strong>
                        {t('metals.incomingSummary.pmpValue', {
                            value: formatNumber(section.pmp, 2),
                        })}
                        {' / '}
                        {t('metals.incomingSummary.pmpValueRon', {
                            value: formatNumber(section.pmpRon, 2),
                        })}
                    </Text>
                </Text>
            }
        >
            <Table<IncomingSummaryRow>
                size="small"
                rowKey="key"
                pagination={false}
                dataSource={section.rows}
                columns={sectionColumns(t, section.metal)}
                summary={() => <SectionTotals t={t} section={section} />}
            />
        </Card>
    );
}

function IncomingSummary({ t, groups }: Readonly<Props>) {
    const sections = buildIncomingSummary(groups);
    return (
        <div style={{ marginTop: 24 }} data-testid="incoming-summary">
            <Title level={4}>{t('metals.summary')}</Title>
            {sections.length === 0 ? (
                <Empty description={t('metals.incomingSummary.empty')} />
            ) : (
                <Row gutter={[16, 16]}>
                    {sections.map(section => (
                        <Col key={section.metal} xs={24} xxl={12}>
                            <SectionCard t={t} section={section} />
                        </Col>
                    ))}
                </Row>
            )}
        </div>
    );
}

export default IncomingSummary;
